import { usePlainList } from '@inmagik/react-crud'
import classNames from 'classnames'
import { Field, getIn, useField, useFormikContext } from 'formik'
import { CSSProperties, forwardRef, InputHTMLAttributes, useMemo, useState } from 'react'
import { Button, Form } from 'react-bootstrap'
import Select from 'react-select'
import AsyncSelect from 'react-select/async'

// const MAX_SIZE = 10 * 1024 * 1024 // 10 MB

interface FieldProps {
  name: string
  required?: boolean
  label: string
  as?: string
  type?: string
  component?: any
  children?: any
  value?: any
  disabled?: boolean
  readonly?: boolean
  style?: CSSProperties
  // Tastiera mostrata sui dispositivi mobili (es. "numeric", "tel", "email")
  inputMode?: InputHTMLAttributes<HTMLInputElement>['inputMode']
  autoComplete?: string
}

export function VField({
  name,
  label,
  as,
  type,
  component,
  children,
  required = false,
  disabled = false,
  readonly = false,
  style,
  inputMode,
  autoComplete,
}: FieldProps) {
  const { touched, errors, values } = useFormikContext()

  const className = useMemo(() => {
    if (as === 'select') {
      return getIn(errors, name) && getIn(touched, name) ? 'is-invalid form-select' : 'form-select'
    }
    if (type === 'checkbox') {
      return getIn(errors, name) && getIn(touched, name) ? 'is-invalid form-check-input' : 'form-check-input'
    }
    return getIn(errors, name) && getIn(touched, name) ? 'is-invalid form-control' : 'form-control'
  }, [as, type, errors, touched, name])

  return (
    <div className="form-group mb-3">
      <label className="label-m label-field">
        {label} {required && <span className="text-danger ms-1">*</span>}
      </label>
      <div className="text-danger error-field">
        {getIn(errors, name) && getIn(touched, name) && getIn(errors, name)}
      </div>
      <div>
        {!readonly && (
          <Field
            name={name}
            className={className}
            as={as}
            type={type}
            component={component}
            disabled={disabled}
            style={style}
            inputMode={inputMode}
            autoComplete={autoComplete}
          >
            {children}
          </Field>
        )}
        {readonly && <Form.Control type={type} readOnly plaintext value={getIn(values, name)} />}
      </div>
    </div>
  )
}

export function HField({ name, label, as, type, component, children }: FieldProps) {
  const { touched, errors } = useFormikContext()

  return (
    <div className="form-group row mb-3">
      <label className="col-sm-2 col-form-label">{label}</label>
      <div className="col-sm-10">
        <Field name={name} className="form-control" as={as} type={type} component={component}>
          {children}
        </Field>
        <div className="text-danger error-field">
          {getIn(errors, name) && getIn(touched, name) && getIn(errors, name)}
        </div>
      </div>
    </div>
  )
}

interface FieldFileProps extends InputHTMLAttributes<HTMLInputElement> {
  name: string
  required?: boolean
  label: string
  as?: string
  type?: string
  component?: any
  children?: any
  value?: any
  disabled?: boolean
  style?: CSSProperties
  accept?: string
}

export const VFieldFile = forwardRef<HTMLInputElement, FieldFileProps>(
  (
    {
      name,
      label,
      as,
      type,
      component,
      children,
      required = false,
      disabled = false,
      readOnly = false,
      style,
      ...props
    },
    ref
  ) => {
    const { touched, errors, values, setFieldValue, setFieldError } = useFormikContext()

    const className = useMemo(() => {
      if (as === 'select') {
        return getIn(errors, name) && getIn(touched, name) ? 'is-invalid form-select' : 'form-select'
      }
      if (type === 'checkbox') {
        return getIn(errors, name) && getIn(touched, name) ? 'is-invalid form-check-input' : 'form-check-input'
      }
      return getIn(errors, name) && getIn(touched, name) ? 'is-invalid form-control' : 'form-control'
    }, [as, type, errors, touched, name])

    const state: { action: 'download' | 'upload' | 'block'; message: string } = useMemo(() => {
      if (readOnly && !(values as any)[name]) {
        return { action: 'block', message: 'Nessun file esistente' }
      }
      if (readOnly && (values as any)[name] && typeof (values as any)[name] === 'string') {
        return { action: 'download', message: 'Scarica file' }
      }
      if (!readOnly && (values as any)[name] && typeof (values as any)[name] === 'string') {
        return { action: 'upload', message: `File attuale: ${(values as any)[name]}` }
      }
      if ((values as any)[name] instanceof File) {
        return { action: 'upload', message: `File caricato: ${(values as any)[name].name}` }
      }
      return { action: 'upload', message: 'Carica file' }
    }, [values, name, readOnly])

    // const handleChange = (name: string, file: File) => {
    //   if (file.size > MAX_SIZE) {
    //     setFieldError(name, 'Il file è troppo grande (max 10MB)')
    //     return
    //   }
    //   setFieldError(name, undefined)
    //   setFieldValue(name, file)
    // }

    return (
      <div className="form-group mb-3">
        <label className="label-m label-field">
          {label} {required && <span className="text-danger ms-1">*</span>}
        </label>
        <div className="text-danger error-field">{getIn(errors, name)}</div>
        <div className="mt-3 d-flex align-items-center">
          <Button
            variant="outline-secondary"
            className="text-secondary d-flex align-items-center"
            onClick={() => {
              if (state.action === 'download') {
                const file = (values as any)[name]
                const isPdf = file.toLowerCase().endsWith('.pdf')
                if (isPdf) {
                  window.open(file, '_blank')
                } else {
                  const link = document.createElement('a')
                  link.href = file
                  link.download = file.split('/').pop()
                  document.body.appendChild(link)
                  link.click()
                  document.body.removeChild(link)
                }
              } else if (state.action === 'upload') {
                if (ref && 'current' in ref && ref.current && !readOnly) {
                  ref.current.click()
                }
              }
            }}
            disabled={disabled}
          >
            <span className="material-icons">{state.action}</span>
          </Button>
          <span className="ms-2 text-truncate">{state.message}</span>

          <input
            ref={ref}
            type="file"
            className={classNames('d-none', className)}
            onChange={async (e) => {
              if (e.target.files && e.target.files[0]) {
                setFieldValue(name, e.target.files[0])
                // handleChange(name, e.target.files[0])
              }
            }}
            {...props}
          />
        </div>
      </div>
    )
  }
)

// react-select field with fetch options

export function VAutocompleteField({
  name,
  label,
  required = false,
  fetchOptions,
  getOptionLabel,
  getOptionValue,
  isMulti = false,
  disabled = false,
}: {
  name: string
  label: string
  required?: boolean
  fetchOptions: (inputValue: string) => Promise<any[]>
  getOptionLabel: (option: any) => string
  getOptionValue: (option: any) => string
  isMulti?: boolean
  disabled?: boolean
}) {
  const { touched, errors, setFieldValue, values } = useFormikContext()
  const [selectedOption, setSelectedOption] = useState<any>(null)

  const className = useMemo(() => {
    return getIn(errors, name) && getIn(touched, name) ? 'is-invalid' : ''
  }, [errors, touched, name])

  const handleChange = (selectedOption: any) => {
    setSelectedOption(selectedOption)
    setFieldValue(
      name,
      isMulti ? selectedOption.map((option: any) => getOptionValue(option)) : selectedOption ? selectedOption.value : ''
    )
  }

  return (
    <div className="form-group mb-3">
      <label className="label-m label-field">
        {label} {required && <span className="text-danger ms-1">*</span>}
      </label>
      <div className="text-danger error-field">
        {getIn(errors, name) && getIn(touched, name) && getIn(errors, name)}
      </div>
      <AsyncSelect
        isMulti={isMulti}
        cacheOptions
        defaultOptions
        className={className}
        placeholder="Seleziona..."
        loadOptions={(inputValue) => {
          const data = fetchOptions(inputValue)
          return data
        }}
        onInputChange={(inputValue) => fetchOptions(inputValue)}
        onChange={handleChange}
        isClearable
        isDisabled={disabled}
        value={selectedOption}
      />
    </div>
  )
}

interface AutoCompleteFieldProps {
  label?: string
  required?: boolean
  value?: any
  url?: string
  isMulti?: boolean
  isDisabled?: boolean
  keyQuery?: string
  params?: any
  isRequired?: boolean
  name: string
  addOption?: boolean
  onAddOptionOpen?: () => void
  filterName?: string
  onChange?: (value: any) => void
}

export default function AutoCompleteField({
  value,
  url = '/progetti/options/',
  isMulti = false,
  isDisabled = false,
  params = {},
  name,
  filterName = 'id',
  onChange,
  label,
  required,
  keyQuery,
}: AutoCompleteFieldProps) {
  const [search, setSearch] = useState<string>('')
  const [focus, setFocus] = useState(false)

  const valueToSend = useMemo(() => {
    if (!isMulti) {
      return value
    }
    if (!value || value.length === 0) {
      return []
    }
    const finalValue = value.join(',')
    return finalValue
  }, [isMulti, value])

  const paramsForItems = useMemo(() => {
    return {
      ...params,
      search,
    }
  }, [search, params])

  const { data: items, isFetching: isFetchingPrimary } = usePlainList(
    url,
    {
      ...paramsForItems,
    },
    {
      enabled: !!search || focus,
    }
  )

  const paramsToSend = useMemo(() => {
    if (valueToSend) {
      if (!isMulti) {
        if (filterName === 'id') {
          return {
            id: valueToSend,
          }
        } else {
          return {
            [filterName]: valueToSend,
          }
        }
      } else {
        return {
          ids: valueToSend,
        }
      }
    }
  }, [isMulti, filterName, valueToSend])

  const { data: values, isFetching } = usePlainList(
    url,
    {
      ...paramsToSend,
    } as any,
    {
      enabled: !!valueToSend || focus,
    }
  )

  const valueForSelect = useMemo(() => {
    if (!value || value.length === 0 || isFetching) {
      return []
    }
    return values?.map((v: any) => ({
      value: String(v.value),
      label: v.label,
    }))
  }, [values, value, isFetching])

  const [field] = useField(name)
  const { errors, touched, setFieldValue } = useFormikContext()

  const error = getIn(errors, name)
  const isTouched = getIn(touched, name)

  return (
    <div className="form-group mb-2">
      {label && (
        <label className="label-m label-field mb-1">
          {label}
          {required && <span className="text-danger">{' *'}</span>}
        </label>
      )}
      <div className="text-danger error-field">
        {getIn(errors, name) && getIn(touched, name) && getIn(errors, name)}
      </div>
      <Select
        isMulti={isMulti}
        isSearchable
        isClearable
        placeholder={''}
        options={items ?? []}
        {...field}
        onFocus={() => {
          setFocus(true)
        }}
        openMenuOnClick={true}
        hideSelectedOptions={true}
        isDisabled={isDisabled}
        noOptionsMessage={() => 'Nessun risultato'}
        onChange={(value: any) => {
          if (onChange) onChange(value)
          if (!isMulti) {
            setFieldValue(field.name, value?.value ?? null)
          } else {
            setFieldValue(
              field.name,
              value?.map((v: any) => Number(v.value))
            )
          }
        }}
        isLoading={isFetchingPrimary}
        onInputChange={(value) => {
          setSearch(value)
        }}
        value={valueForSelect}
        menuPortalTarget={document.body}
        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
      />
    </div>
  )
}
